import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ThreadMenuContent } from "@/components/Chat/header/ThreadMenu";
import { TypingAnimation } from "@/components/ui/magicui";
import { SidebarMenuButton } from "@/components/ui/sidebar";
import { useChat } from "@/contexts/ChatContext";
import { cn } from "@/lib/utils";
import { isThreadBeingDeleted } from "@/lib/utils/threadDeletionQueue";
import { useAuthStore, useThreadStore } from "@/store";
import { Loader2, MoreHorizontal } from "lucide-react";
import { useLocation } from "react-router-dom";
import { TbPinFilled } from "react-icons/tb";

/**
 * Individual history thread item component
 */
export function HistoryItem({
  thread,
  openDropdownId,
  setOpenDropdownId,
  isThreadNew,
  handleThreadSelect,
  startRenaming,
  handleThreadDelete,
  handleThreadShare,
  handleToggleBookmark,
  handleThreadClone,
  handleMoveToProject,
}) {
  const location = useLocation();
  const { activeDeepResearchThreadId } = useThreadStore();
  const { workspaces } = useAuthStore();
  const chatContext = useChat();
  const activeDeepResearch = chatContext?.activeDeepResearch;

  const isActive = location.pathname === `/chat/${thread.id}`;
  const isDeleting = isThreadBeingDeleted(thread.id);
  const isDeepResearchActive =
    activeDeepResearch && activeDeepResearchThreadId === thread.id;

  return (
    <div
      key={`${thread?.id}-${thread.updated_at || Date.now()}`}
      className={cn(
        "w-full max-w-full pl-2 py-0.5 group/thread",
        isDeleting && "opacity-50 pointer-events-none",
        isDeepResearchActive && "opacity-50 pointer-events-none"
      )}
    >
      <SidebarMenuButton
        className={cn(
          "flex items-center w-full max-w-full pr-0 text-sidebar-foreground group",
          isActive
            ? "bg-sidebar-accent text-sidebar-accent-foreground"
            : "bg-transparent hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        )}
        onClick={(e) => {
          if (
            e.target.closest(".dropdown-menu-item") ||
            isDeleting ||
            isDeepResearchActive
          ) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }

          if (isDeleting || isDeepResearchActive) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }

          e.stopPropagation();
          handleThreadSelect(thread);
        }}
      >
        <div className="flex items-center w-full max-w-full">
          <div className="flex items-center flex-1 min-w-0 mr-2 max-w-[180px]">
            {/* Show loading spinner if deep research is active for this thread */}
            {isDeepResearchActive && (
              <Loader2 className="h-4 w-4 mr-2 text-purple-400 animate-spin flex-shrink-0" />
            )}

            {isThreadNew(thread.id) ? (
              <TypingAnimation
                duration={40}
                className="truncate min-w-0 block overflow-hidden text-ellipsis whitespace-nowrap max-w-[220px] text-sm"
                as="span"
              >
                {thread?.title || "Untitled Chat"}
              </TypingAnimation>
            ) : (
              <span
                className={cn(
                  "truncate min-w-0 block overflow-hidden text-ellipsis whitespace-nowrap max-w-[220px]",
                  isDeleting && "opacity-50 line-through",
                  isDeepResearchActive && "opacity-50"
                )}
                title={thread?.title || "Untitled Chat"}
              >
                {isDeleting
                  ? "Deleting..."
                  : isDeepResearchActive
                    ? "Deep research in progress..."
                    : thread?.title || "Untitled Chat"}
              </span>
            )}
          </div>

          <div className="flex-shrink-0">
            <DropdownMenu
              open={openDropdownId === thread.id}
              onOpenChange={(open) => {
                if (open) {
                  setOpenDropdownId(thread.id);
                } else {
                  setOpenDropdownId(null);
                }
              }}
            >
              <DropdownMenuTrigger asChild>
                <div
                  role="button"
                  aria-label="Chat options"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className={cn(
                    "h-6 w-6 flex items-center justify-center bg-transparent rounded-sm focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:relative focus-visible:z-[100] outline-none transition-opacity duration-200 hover:bg-sidebar-accent cursor-pointer flex-shrink-0 group/trigger",
                    thread.metadata?.bookmarked
                      ? "opacity-100"
                      : "opacity-0 group-hover/thread:opacity-100 group-focus-within/thread:opacity-100 focus:opacity-100"
                  )}
                >
                  {/* Pin indicator - shown when pinned and not hovering trigger */}
                  {thread.metadata?.bookmarked && (
                    <TbPinFilled className="h-3.5 w-3.5 text-sidebar-foreground/60 group-hover/trigger:hidden" />
                  )}
                  {/* Menu icon - shown on hover or when not pinned */}
                  <MoreHorizontal
                    className={cn(
                      "h-4 w-4 text-sidebar-foreground/60 hover:text-sidebar-foreground transition-colors",
                      thread.metadata?.bookmarked
                        ? "hidden group-hover/trigger:block"
                        : "block"
                    )}
                  />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                side="right"
                alignOffset={-5}
                sideOffset={5}
                className="w-[200px] z-[9999] rounded-2xl bg-background/80 backdrop-blur-md border border-border/50"
                style={{ zIndex: 9999 }}
                forceMount
                onClick={(e) => e.stopPropagation()}
              >
                <ThreadMenuContent
                  thread={thread}
                  workspaces={workspaces}
                  isDeleting={isDeleting}
                  usePinTerminology={true}
                  itemClassName="rounded-2xl"
                  onToggleBookmark={handleToggleBookmark}
                  onRename={startRenaming}
                  onMoveToProject={handleMoveToProject}
                  onShare={handleThreadShare}
                  onClone={handleThreadClone}
                  onDelete={handleThreadDelete}
                  onClose={() => setOpenDropdownId(null)}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </SidebarMenuButton>
    </div>
  );
}
